import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import DatePickerAdvance from "@/components/DatePicker";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Download, 
  FileText, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Loader2,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DatePicker = DatePickerAdvance;

type DateRange = {
  from?: Date;
  to?: Date;
};

interface StatData {
  amount: number;
  count: number;
}

export default function ContributionsReport() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<DateRange | undefined>(undefined);
  const [endDate, setEndDate] = useState<DateRange | undefined>(undefined);
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("all");

  // Fetch groups for filter
  const { data: groups = [] } = useQuery(["groups"], async () => {
    const { data } = await api.get("/groups");
    return data.results || [];
  });

  // Fetch branches for filter
  const { data: branches = [] } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results || [];
  });

  // Fetch districts for filter
  const { data: districts = [] } = useQuery(["districts"], async () => {
    const { data } = await api.get("/districts");
    return data.results || [];
  });

  // Fetch contributions with filters
  const contributionsQuery = useQuery({
    queryKey: ["contributions-report", { startDate, endDate, selectedGroup, selectedBranch, selectedDistrict }],
    queryFn: async () => {
      const filters: any[] = [];

      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      if (selectedBranch !== "all") {
        filters.push({
          field: "branch.name",
          operator: "eq",
          value: selectedBranch,
        });
      }

      if (selectedDistrict !== "all") {
        filters.push({
          field: "branch.district.name",
          operator: "eq",
          value: selectedDistrict,
        });
      }

      if (startDate?.from) {
        filters.push({
          field: "createdAt",
          operator: "gte",
          value: format(startDate.from, "yyyy-MM-dd"),
        });
      }

      if (endDate?.to) {
        filters.push({
          field: "createdAt",
          operator: "lte",
          value: format(endDate.to, "yyyy-MM-dd"),
        });
      }

      const { data } = await api.get("/contributions", {
        params: {
          page_size: 1000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
        });

      return data.results || [];
    },
  });

  const contributions = contributionsQuery.data || [];

  // Calculate statistics
  const stats = {
    totalContributions: contributions.length,
    totalDeposits: contributions.reduce((sum, c) => sum + (Number(c.depositAmount) || 0), 0),
    totalSolidarity: contributions.reduce((sum, c) => sum + (Number(c.solidarityAmount) || 0), 0),
    uniqueMembers: new Set(contributions.map(c => c.member?.id).filter(Boolean)).size,
    uniqueGroups: new Set(contributions.map(c => c.group?.id).filter(Boolean)).size,
  };

  stats.totalDeposits = Math.round(stats.totalDeposits);
  stats.totalSolidarity = Math.round(stats.totalSolidarity);

  const totalAmount = stats.totalDeposits + stats.totalSolidarity;

  // Group by payment method
  const paymentMethodStats: Record<string, StatData> = contributions.reduce((acc, c) => {
    const method = c.paymentMethod?.name || "Unknown";
    if (!acc[method]) {
      acc[method] = { count: 0, amount: 0 };
    }
    acc[method].count++;
    acc[method].amount += (Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0);
    return acc;
  }, {} as Record<string, StatData>);

  // Group by group
  const groupStats: Record<string, StatData> = contributions.reduce((acc, c) => {
    const groupName = c.group?.name || "Unknown";
    if (!acc[groupName]) {
      acc[groupName] = { count: 0, amount: 0 };
    }
    acc[groupName].count++;
    acc[groupName].amount += (Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0);
    return acc;
  }, {} as Record<string, StatData>);

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (contributions.length === 0) {
        toast.error("No data to export");
        return;
      }

      const data = contributions.map((c) => ({
        Date: c.createdAt ? format(new Date(c.createdAt), "yyyy-MM-dd HH:mm") : "",
        Member: c.member?.fullNames || "",
        Group: c.group?.name || "",
        Branch: c.branch?.name || "",
        District: c.branch?.district?.name || "",
        "Deposit Amount": Number(c.depositAmount) || 0,
        "Solidarity Amount": Number(c.solidarityAmount) || 0,
        "Total Amount": (Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0),
        "Payment Method": c.paymentMethod?.name || "",
        "Received By": c.receivedBy?.name || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Contributions");

      const filename = `contributions-report-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export Excel: " + (error.message || "Unknown error"));
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (contributions.length === 0) {
        toast.error("No data to export");
        return;
      }

      const doc = new jsPDF('landscape');
      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      // Header
      doc.setFontSize(16);
      doc.text(`Contributions Report - ${groupName}`, 14, 10);
      doc.setFontSize(12);
      if (startDate?.from && endDate?.to) {
        doc.text(
          `${format(startDate.from, "MMM dd, yyyy")} - ${format(endDate.to, "MMM dd, yyyy")}`,
          14,
          18
        );
      }
      doc.text(`Generated: ${format(new Date(), "MMM dd, yyyy")}`, 14, 24);

      // Summary
      doc.setFontSize(10);
      doc.text(`Total Contributions: ${stats.totalContributions}`, 14, 32);
      doc.text(`Total Amount: ${totalAmount.toLocaleString()} RWF`, 14, 38);

      // Prepare table data
      const tableData = contributions.map((c) => [
        c.createdAt ? format(new Date(c.createdAt), "MMM dd, yyyy") : "",
        c.member?.fullNames || "",
        c.group?.name || "",
        c.branch?.name || "",
        (Number(c.depositAmount) || 0).toLocaleString(),
        (Number(c.solidarityAmount) || 0).toLocaleString(),
        ((Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0)).toLocaleString(),
        c.paymentMethod?.name || "",
      ]);

      const headers = [
        "Date",
        "Member",
        "Group",
        "Branch",
        "Deposit",
        "Solidarity",
        "Total",
        "Payment Method",
      ];

      // Add table
      autoTable(doc as any, {
        head: [headers],
        body: tableData,
        startY: 45,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
      });

      const filename = `contributions-report-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success("PDF file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export PDF: " + (error.message || "Unknown error"));
    }
  };

  // Print report
  const handlePrint = () => {
    try {
      if (contributions.length === 0) {
        toast.error("No data to print");
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print the report");
        return;
      }

      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      const dateRangeStr = startDate?.from && endDate?.to
        ? format(startDate.from, "MMM dd, yyyy") + " - " + format(endDate.to, "MMM dd, yyyy")
        : "All Time";

      const generatedDate = format(new Date(), "MMM dd, yyyy");
      const generatedTime = format(new Date(), "HH:mm:ss");
      
      const tableRows = contributions.map((c) => {
        const createdAt = c.createdAt ? format(new Date(c.createdAt), "MMM dd, yyyy") : "—";
        const memberName = c.member?.fullNames || "—";
        const groupName = c.group?.name || "—";
        const branchName = c.branch?.name || "—";
        const deposit = (Number(c.depositAmount) || 0).toLocaleString();
        const solidarity = (Number(c.solidarityAmount) || 0).toLocaleString();
        const total = ((Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0)).toLocaleString();
        const paymentMethod = c.paymentMethod?.name || "—";
        return "<tr>" +
          "<td>" + createdAt + "</td>" +
          "<td>" + memberName + "</td>" +
          "<td>" + groupName + "</td>" +
          "<td>" + branchName + "</td>" +
          "<td class=\"text-right\">" + deposit + "</td>" +
          "<td class=\"text-right\">" + solidarity + "</td>" +
          "<td class=\"text-right\">" + total + "</td>" +
          "<td>" + paymentMethod + "</td>" +
          "</tr>";
      }).join('');

      const htmlContent = "<!DOCTYPE html>" +
        "<html>" +
        "<head>" +
        "<title>Contributions Report - " + groupName + "</title>" +
        "<style>" +
        "@media print { @page { size: landscape; margin: 1cm; } body { margin: 0; padding: 0; } }" +
        "body { font-family: Arial, sans-serif; padding: 20px; }" +
        ".header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }" +
        ".header h1 { margin: 0; font-size: 24px; color: #333; }" +
        ".header h2 { margin: 5px 0; font-size: 18px; color: #666; }" +
        ".summary { display: flex; justify-content: space-around; margin: 20px 0; padding: 15px; background-color: #f5f5f5; border-radius: 5px; }" +
        ".summary-item { text-align: center; }" +
        ".summary-item strong { display: block; font-size: 18px; color: #333; }" +
        ".summary-item span { font-size: 14px; color: #666; }" +
        "table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }" +
        "th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }" +
        "th { background-color: #3b82f6; color: white; font-weight: bold; text-align: center; }" +
        "tr:nth-child(even) { background-color: #f9fafb; }" +
        ".text-right { text-align: right; }" +
        ".footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }" +
        "</style>" +
        "</head>" +
        "<body>" +
        "<div class=\"header\">" +
        "<h1>Contributions Report - " + groupName + "</h1>" +
        "<h2>" + dateRangeStr + "</h2>" +
        "<p>Generated: " + generatedDate + "</p>" +
        "</div>" +
        "<div class=\"summary\">" +
        "<div class=\"summary-item\"><strong>" + stats.totalContributions + "</strong><span>Total Contributions</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalDeposits.toLocaleString() + " RWF</strong><span>Total Deposits</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalSolidarity.toLocaleString() + " RWF</strong><span>Total Solidarity</span></div>" +
        "<div class=\"summary-item\"><strong>" + totalAmount.toLocaleString() + " RWF</strong><span>Total Amount</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.uniqueMembers + "</strong><span>Active Members</span></div>" +
        "</div>" +
        "<table>" +
        "<thead>" +
        "<tr><th>Date</th><th>Member</th><th>Group</th><th>Branch</th><th class=\"text-right\">Deposit</th><th class=\"text-right\">Solidarity</th><th class=\"text-right\">Total</th><th>Payment Method</th></tr>" +
        "</thead>" +
        "<tbody>" + tableRows + "</tbody>" +
        "</table>" +
        "<div class=\"footer\">" +
        "<p>Contributions Report generated on " + generatedDate + " at " + generatedTime + "</p>" +
        "</div>" +
        "</body>" +
        "</html>";

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success("Print dialog opened");
        }, 250);
      };
    } catch (error: any) {
      toast.error("Failed to print: " + (error.message || "Unknown error"));
    }
  };

  const clearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedGroup("all");
    setSelectedBranch("all");
    setSelectedDistrict("all");
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("pages.contributionsReport.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("pages.contributionsReport.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToPDF}
            disabled={contributions.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.pdf")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={contributions.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.excel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={contributions.length === 0}
          >
            <Printer className="h-4 w-4 mr-1" />
            {t("common.print")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.contributionsReport.filters.title")}</CardTitle>
          <CardDescription>{t("pages.contributionsReport.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.contributionsReport.filters.startDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate?.from && !startDate?.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate?.from && startDate?.to ? (
                      startDate.from.getTime() === startDate.to.getTime() 
                        ? format(startDate.from, "MMM dd, yyyy")
                        : `${format(startDate.from, "MMM dd, yyyy")} - ${format(startDate.to, "MMM dd, yyyy")}`
                    ) : startDate?.from ? (
                      format(startDate.from, "MMM dd, yyyy")
                    ) : startDate?.to ? (
                      format(startDate.to, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={startDate} setDate={(date) => setStartDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.contributionsReport.filters.endDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate?.to && !endDate?.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate?.from && endDate?.to ? (
                      endDate.from.getTime() === endDate.to.getTime() 
                        ? format(endDate.to, "MMM dd, yyyy")
                        : `${format(endDate.from, "MMM dd, yyyy")} - ${format(endDate.to, "MMM dd, yyyy")}`
                    ) : endDate?.to ? (
                      format(endDate.to, "MMM dd, yyyy")
                    ) : endDate?.from ? (
                      format(endDate.from, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={endDate} setDate={(date) => setEndDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.contributionsReport.filters.district")}</label>
              <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.contributionsReport.filters.allDistricts")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.contributionsReport.filters.allDistricts")}</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.contributionsReport.filters.branchSector")}</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.contributionsReport.filters.allBranches")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.contributionsReport.filters.allBranches")}</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.contributionsReport.filters.group")}</label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.contributionsReport.filters.allGroups")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.contributionsReport.filters.allGroups")}</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(startDate || endDate || selectedGroup !== "all" || selectedBranch !== "all" || selectedDistrict !== "all") && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t("pages.contributionsReport.filters.clearFilters")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.contributionsReport.stats.totalContributions")}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalContributions}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.contributionsReport.stats.records")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.contributionsReport.stats.totalDeposits")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalDeposits.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.contributionsReport.stats.depositAmount")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.contributionsReport.stats.totalSolidarity")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalSolidarity.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.contributionsReport.stats.solidarityAmount")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.contributionsReport.stats.totalAmount")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalAmount.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.contributionsReport.stats.combinedTotal")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.contributionsReport.stats.activeMembers")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueMembers}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("pages.contributionsReport.stats.acrossGroups", { count: stats.uniqueGroups })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Methods */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.contributionsReport.analytics.byPaymentMethod")}</CardTitle>
            <CardDescription>{t("pages.contributionsReport.analytics.byPaymentMethodDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(paymentMethodStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(paymentMethodStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .map(([method, data]) => (
                    <div key={method} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{method}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {t("pages.contributionsReport.analytics.transactions", { count: data.count })}
                        </span>
                      </div>
                      <div className="text-sm font-medium">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Groups */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.contributionsReport.analytics.topContributingGroups")}</CardTitle>
            <CardDescription>{t("pages.contributionsReport.analytics.topContributingGroupsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(groupStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .slice(0, 5)
                  .map(([group, data]) => (
                    <div key={group} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {group}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          ({data.count})
                        </span>
                      </div>
                      <div className="text-sm font-medium">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contributions Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("pages.contributionsReport.table.title")}</CardTitle>
          <CardDescription>
            {contributions.length === 1 
              ? t("pages.contributionsReport.table.description", { count: contributions.length })
              : t("pages.contributionsReport.table.description_plural", { count: contributions.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {contributionsQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
      ) : contributions.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("pages.contributionsReport.table.noContributions")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("pages.contributionsReport.table.adjustFilters")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.contributionsReport.table.date")}</TableHead>
                    <TableHead>{t("pages.contributionsReport.table.member")}</TableHead>
                    <TableHead>{t("pages.contributionsReport.table.group")}</TableHead>
                    <TableHead>{t("pages.contributionsReport.table.branch")}</TableHead>
                    <TableHead className="text-right">{t("pages.contributionsReport.table.deposit")}</TableHead>
                    <TableHead className="text-right">{t("pages.contributionsReport.table.solidarity")}</TableHead>
                    <TableHead className="text-right">{t("pages.contributionsReport.table.total")}</TableHead>
                    <TableHead>{t("pages.contributionsReport.table.paymentMethod")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contributions.map((c) => {
                    const total = (Number(c.depositAmount) || 0) + (Number(c.solidarityAmount) || 0);
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="whitespace-nowrap">
                          {c.createdAt ? format(new Date(c.createdAt), "MMM dd, yyyy") : "—"}
                        </TableCell>
                        <TableCell>{c.member?.fullNames || "—"}</TableCell>
                        <TableCell>{c.group?.name || "—"}</TableCell>
                        <TableCell>{c.branch?.name || "—"}</TableCell>
                        <TableCell className="text-right">
                          {(Number(c.depositAmount) || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {(Number(c.solidarityAmount) || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {total.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{c.paymentMethod?.name || "—"}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
      )}
        </CardContent>
      </Card>
</div>
  );
}
